import { inject, Injectable, signal } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import type {
  TenderFormEvaluation,
  TenderLot,
} from '../../../api/interfaces';
import type { RequirementInput } from '../../../pages/new-compiling-dashboard/requirements-to-slots';

/** The tender this answer is being compiled against. */
export interface TenderInfo {
  title: string;
  attachments: { id: string; fileName: string; url: string }[];
  expiryDate: Date;
  publicationDate: Date;
  concessionPeriodYears: number;
  concessionPeriodMaxYears: number;
  contractingAuthority?: string;
  extractedRequirements?: {
    envelopeA: RequirementInput[];
    envelopeB: RequirementInput[];
    envelopeC: RequirementInput[];
  } | null;
}

/** Identity of the answer being compiled, and its applicant when loaded. */
export interface TenderFormAnswerRef {
  id: string;
  tenderId: string;
  applicant?: {
    id: string;
    private: {
      firstName: string;
      lastName: string;
      dateOfBirth: string;
      placeOfBirth: string;
      citizenship: string;
      fiscalCode: string;
      address: string;
    };
    company: {
      companyName: string;
      vatNumber: string;
      taxId: string;
      registeredOffice: string;
    };
    type: string;
  };
}

/**
 * Everything about *which* tender and *whose* answer is on screen — as opposed
 * to the structure of the form itself, which is `CompilingFormService`.
 */
@Injectable()
export class TenderContextService {
  private readonly fb = inject(FormBuilder);

  readonly tenderInfo = signal<TenderInfo | undefined>(undefined);
  readonly tenderLotDetails = signal<TenderLot | undefined>(undefined);
  readonly tenderFormAnswer = signal<TenderFormAnswerRef | undefined>(
    undefined,
  );

  readonly applicantForm = this.fb.group({
    applicantType: [''],
    applicantFirstName: [''],
    applicantLastName: [''],
    applicantDateOfBirth: [''],
    applicantPlaceOfBirth: [''],
    applicantFiscalCode: [''],
    applicantCitizenship: [''],
    applicantAddress: [''],
    applicantCompanyName: [''],
    applicantVatNumber: [''],
    applicantTaxId: [''],
    applicantId: [''],
    applicantRegisteredOffice: [''],
  });

  /** Per-widget instructions the applicant wrote for the AI autofill. */
  readonly questionInstructions = signal<{ [widgetId: string]: string }>({});
  readonly evaluationResult = signal<TenderFormEvaluation | null>(null);
}
